import { z } from 'zod';

export const ThingShadowSchema = z.object({
  state: z.object({
    desired: z.record(z.string(), z.any()).optional(),
    reported: z.record(z.string(), z.any()).optional(),
    delta: z.record(z.string(), z.any()).optional(),
  }),
  version: z.number().optional(),
  clientToken: z.string().max(64).optional(),
});
export type ThingShadow = z.infer<typeof ThingShadowSchema>;

export const FleetShadowSchema = z.object({
  desired: z.record(z.string(), z.any()).optional(),
  reported: z.record(z.string(), z.any()).optional(),
  delta: z.record(z.string(), z.any()).optional(),
  version: z.number().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});
export type FleetShadow = z.infer<typeof FleetShadowSchema>;

export const idleConfigShadowName = 'IdleConfig';
export const IdleConfigShadowDataSchema = z.object({
  url: z.url(),
  // Grrr. We use this schema both for SENDING TO as well as RECEVING data FROM the shadow.
  // But: `null` in SENDING means "delete this property from the shadow", whereas `null` can NEVER be received:
  // https://docs.aws.amazon.com/iot/latest/developerguide/device-shadow-data-flow.html#deleting-thing-data
  // So the actually correct design would be to have TWO separate schemas: one for sending updates to the shadow.
  // But we just use `.partial()` when receiving data to allow for `null` values, which is a bit hacky. 🫣
  config: z.string().nullable().optional(),
});
export type IdleConfigShadowData = z.infer<typeof IdleConfigShadowDataSchema>;

export const sessionShadowName = 'Session';
/** Named shadows whose desired/reported state is rendered by the fleet dashboard. */
export type FleetShadowName = typeof sessionShadowName | typeof idleConfigShadowName;
export const SessionShadowDataSchema = z
  .object({
    sessionId: z.string().uuid().nullable(),
    // Unix timestamp in milliseconds; the shadow field name is part of the wire contract.
    expiresAt: z.number().int().min(0).nullable(),
    url: z.string().nullable(),
    config: z.string().nullable(),
  })
  .partial();
export type SessionShadowData = z.infer<typeof SessionShadowDataSchema>;

export const packageStateShadowName = '$package';
export const PackageStateShadowDataSchema = z
  .object({
    kiosk: z.object({
      version: z.string(),
    }),
    chromeOs: z.object({
      version: z.string().nullish(),
    }),
  })
  .partial();
export type PackageStateShadowData = z.infer<typeof PackageStateShadowDataSchema>;

/**
 * Minimum Chrome/ChromeOS major version required to run session mode (DRPT-3946).
 *
 * @remarks
 * Session streaming is only supported on Chrome/ChromeOS >= 111.
 * The kiosk reports its ChromeOS major version in the `$package` shadow
 * (`chromeOs.version`, see {@link PackageStateShadowDataSchema}).
 */
export const SESSION_CAPABLE_CHROME_OS_MAJOR = 111;

/**
 * Returns whether a parsed ChromeOS major version meets the session capability floor.
 *
 * @param chromeOsMajor - Parsed major version, or `null` when the version is absent or unparsable
 * @returns `true` only when the major version is known and at or above {@link SESSION_CAPABLE_CHROME_OS_MAJOR}
 */
export function meetsSessionFloor(chromeOsMajor: number | null): boolean {
  return chromeOsMajor !== null && chromeOsMajor >= SESSION_CAPABLE_CHROME_OS_MAJOR;
}

/**
 * Parses the ChromeOS major version from a version string. Accepts a major-only or
 * dotted-numeric version and rejects non-numeric or trailing content.
 *
 * @example
 * parseChromeOsMajor('111') // 111
 * parseChromeOsMajor('111.0.5563.146') // 111
 * parseChromeOsMajor('111abc') // null
 * parseChromeOsMajor('abc') // null
 * parseChromeOsMajor(null) // null
 */
export function parseChromeOsMajor(version: string | null | undefined): number | null {
  if (version == null) return null;
  const major = /^(\d+)(?:\.\d+)*$/.exec(version)?.[1];
  return major === undefined ? null : Number(major);
}

/**
 * Shape of the `$package` member as it appears in a Fleet Index search document.
 * Differs from {@link FleetShadowSchema}: no `metadata`, no `desired`/`delta` —
 * observed live: `{"reported":{"kiosk":{"version":"1.12.0"},"chromeOs":{"version":"149"}},"hasDelta":false,"version":4}`.
 */
const PackageStateFleetShadowSchema = z.object({
  reported: PackageStateShadowDataSchema.optional(),
  hasDelta: z.boolean().optional(),
  version: z.number().optional(),
});

export const FleetIndexShadowSchema = z.object({
  name: z.object({
    [sessionShadowName]: FleetShadowSchema.optional(),
    [idleConfigShadowName]: FleetShadowSchema.optional(),
    /**
     * `$package` carries the kiosk-reported state. Everything is optional so that
     * device documents without a `$package` shadow still parse.
     */
    [packageStateShadowName]: PackageStateFleetShadowSchema.optional(),
  }),
});

/**
 * Extracts the `$package` member (reported state, hasDelta, version) from a wanted member
 * of a parsed Fleet Index `name` object; `undefined` if the member does not exist or does
 * not match the `$package` shape.
 *
 * @param shadowMember - The `name` member for {@link packageStateShadowName}; accepted as
 * `unknown` so the schema remains the single validation point for externally sourced documents.
 */
export function extractPackageStateFromFleetShadow(
  shadowMember: unknown,
): z.infer<typeof PackageStateFleetShadowSchema> | undefined {
  const parsed = PackageStateFleetShadowSchema.safeParse(shadowMember);
  return parsed.success ? parsed.data : undefined;
}

const IdleConfigShadowStateSchema = z.object({
  url: z.url(),
});

function extractIdleConfigUrlFromState(state: unknown): string | undefined {
  const parsed = IdleConfigShadowStateSchema.safeParse(state);
  return parsed.success ? parsed.data.url : undefined;
}

export function extractIdleConfigUrlFromThingShadow(shadow: ThingShadow): string | undefined {
  return extractIdleConfigUrlFromState(shadow.state.reported);
}

export function extractIdleConfigUrlFromFleetShadow(shadow: FleetShadow | undefined): string | undefined {
  return extractIdleConfigUrlFromState(shadow?.reported);
}
